import { project } from '@/globals/config/helpers/configEnvironment';

/**
 * Formats a number with thousands separators and optional decimal places.
 */
export const formatNumber = ({
	decimals = 0,
	number,
}: {
	decimals?: number;
	number: number;
}): string => {
	const numberString = String(number);
	const [integerString, decimalString = ''] = numberString.split('.');

	const formattedDecimals = decimalString
		.slice(0, decimals)
		.padEnd(decimals, '0');

	const thousandsSeparatorGlyph = ' ';
	const decimalGlyph = project === 'op' ? ',' : '.';
	const thousandSeperationFromLength = 4;
	const chunkSize = 3;

	const formatIntegerPart = (str: string): string => {
		if (str.length <= thousandSeperationFromLength) {
			return str;
		}

		const chunks: string[] = [];
		let remaining = str;

		while (remaining.length > 0) {
			chunks.unshift(remaining.slice(-chunkSize));
			remaining = remaining.slice(0, -chunkSize);
		}

		return chunks.join(thousandsSeparatorGlyph);
	};

	const formattedIntegers = formatIntegerPart(integerString);

	return formattedDecimals.length > 0
		? `${formattedIntegers}${decimalGlyph}${formattedDecimals}`
		: formattedIntegers;
};

/**
 * Capitalizes the first letter of a string.
 */
export const capitalizeFirstLetter = (value: string): string => {
	return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
};

/**
 * Returns the value if it's a string, otherwise returns undefined.
 */
export const stringOnly = (value: unknown): string | undefined => {
	return typeof value === 'string' ? value : undefined;
};

/**
 * Converts a string to kebab-case format.
 */
export const kebabCase = (text = ''): string => {
	return (
		text
			.toLowerCase()
			// Replace all non alphanumeric characters with a dash
			.replace(/[^a-zA-Z0-9]+/g, '-')
			// Trim dashes from start
			.replace(/^-+/, '')
			// Trim dashes from end
			.replace(/-+$/, '')
	);
};

const replacer =
	(data: Record<string, string | number>) => (_match: string, name: string) =>
		String(data?.[name]) || '';

export const interpolateString = (
	string = '',
	data: Record<string, string | number>,
) => string.replace(/\$\{([A-z0-9]*)\}/g, replacer(data));

export const startsWithCaseInsensitive = (
	string: string,
	searchString: string,
) => string.toLowerCase().startsWith(searchString.toLowerCase());
